import { useRequest } from 'features/request/useRequest';
import type { ChapterEnum } from 'gql/graphql';
import { CostGroupEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { workOrderPriceActKC2Gql, workOrderPricesGql } from '../../workOrderQueries';

import { destroyWorkOrderFilters, getWorkOrderWhereFromFilter, workOrderItemFormatter } from './helpers';

interface IWorkOrderPricesProps {
    workOrderId: number;
    chapter: ChapterEnum;
    workOrderObjectId: number;
    workOrderSubObjectId: number;
    reload: any;
    gridApiRef: AgGridReact | null;
}

export const useWorkOrderDataGetter = ({
    workOrderId,
    chapter,
    workOrderObjectId,
    workOrderSubObjectId,
    reload,
    gridApiRef,
}: IWorkOrderPricesProps) => {
    const dispatch = useDispatch();

    const { state }: { state: { actNavInfo?: any } } = useLocation();

    const [filtersWhere, setFiltersWhere] = useState<Record<string, any> | undefined>(undefined);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prevValues) => ({
            and: {
                ...prevValues?.and,
                ...getWorkOrderWhereFromFilter(fieldName, checkedValues),
            },
        }));
    }, []);

    const getWorkOrderDataVariables = useMemo(
        () => ({
            workOrderId,
            chapter,
            workOrderObjectId: workOrderSubObjectId,
            where: filtersWhere,
        }),
        // eslint-disable-next-line
        [reload, workOrderId, chapter, workOrderSubObjectId, filtersWhere],
    );

    const [workOrderPriceListData, , , workOrderPriceListMakeRequest] = useRequest({
        requestId: 'workOrderPriceList',
        query: workOrderPricesGql,
        variables: getWorkOrderDataVariables,
        withEffect: false,
    });

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    `${chapter}_${workOrderObjectId}_${workOrderSubObjectId}`,
                    getDefaultFilterCleaner({ changeFilterHandler, setFiltersWhere }),
                    Boolean(state?.actNavInfo?.actId),
                ),
            }),
            [changeFilterHandler, chapter, state?.actNavInfo, workOrderObjectId, workOrderSubObjectId],
        ),
    );

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'workOrderPriceList',
        requestFn: workOrderPriceListMakeRequest,
        requestVariables: getWorkOrderDataVariables,
    });

    useEffect(() => {
        if (workOrderPriceListData?.uerWorkOrderPrices?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                workOrderPriceListData.uerWorkOrderPrices.items.map(workOrderItemFormatter),
                workOrderPriceListData.uerWorkOrderPrices?.totalCount,
            );
            gridApiRef?.api?.refreshHeader();
        }
    }, [getRowParams, gridApiRef?.api, workOrderPriceListData?.uerWorkOrderPrices]);

    const totalItems = useMemo(
        () => workOrderPriceListData?.uerWorkOrderPrices?.totalCount,
        [workOrderPriceListData?.uerWorkOrderPrices?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    const workOrderPriceGetActsVariables = useMemo(
        () => ({ workOrderId, workOrderRootObjectId: workOrderObjectId, actCostGroup: CostGroupEnum.Cmr }),
        [workOrderId, workOrderObjectId],
    );

    const [workOrderPriceListActs, , , workOrderPriceListActsMakeRequest] = useRequest({
        requestId: 'workOrderPriceListActs',
        query: workOrderPriceActKC2Gql,
        variables: workOrderPriceGetActsVariables,
        withEffect: Boolean(workOrderId && workOrderObjectId),
    });

    const refreshPriceListData = useCallback(() => {
        gridApiRef?.api?.refreshInfiniteCache();

        destroyWorkOrderFilters({
            api: gridApiRef?.api,
            columns: ['code', 'name', 'status', 'unit'],
        });
    }, [gridApiRef?.api]);

    const refreshPriceListActsData = useCallback(() => {
        dispatch(workOrderPriceListActsMakeRequest(workOrderPriceGetActsVariables));
    }, [dispatch, workOrderPriceGetActsVariables, workOrderPriceListActsMakeRequest]);

    useEffect(() => {
        if (reload) {
            refreshPriceListActsData();
            refreshPriceListData();
        }
    }, [refreshPriceListActsData, refreshPriceListData, reload]);

    return {
        workOrderPriceData: dataSource,
        workOrderPriceListActs: workOrderPriceListActs?.uerWorkOrderActsKC2?.items ?? [],
        variables: getWorkOrderDataVariables,
        refreshPriceListData,
        refreshPriceListActsData,
        changeFilterHandler,
        fetchedItems,
    };
};
